import 'alpine-turbo-drive-adapter'
import Alpine from 'alpinejs'
import Sortable from 'sortablejs'

window.Alpine = Alpine

/* eslint-disable sort-keys */

const copyRuleFieldsToContainer = (mainForm, hiddenContainer) => {
    const formData = new FormData(mainForm)
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('rule[')) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        hiddenContainer.appendChild(input)
      }
    }
  },
  registerRuleTestForm = (alpine) => {
    alpine.data('ruleTestForm', () => ({
      loading: false,
      prepareTestFields() {
        const mainForm = document.getElementById('rule-form')
        if (!mainForm) {
          return
        }

        this.$refs.hiddenFields.innerHTML = ''

        copyRuleFieldsToContainer(mainForm, this.$refs.hiddenFields)
      },
    }))
  },
  INITIAL_CONDITION_COUNT = 0,
  // Handles adding nested child condition groups in the rule form.
  // vanilla_nested can't handle recursive templates (a group template containing
  // group templates), so we clone and insert them manually here.
  // See docs/turbo-streams-condition-groups-refactor.md for a potential rework.
  registerConditionGroup = (alpine) => {
    alpine.data('conditionGroup', (initialCount = INITIAL_CONDITION_COUNT) => ({
      conditionCount: initialCount,
      addChildGroup() {
        const template = this.$refs.childGroupTemplate,
          container = this.$refs.childGroupsContainer,
          placeholder = template && template.dataset.placeholder,
          html = placeholder && template.innerHTML.replace(new RegExp(placeholder, 'gu'), Date.now())
        if (!template || !container) {
          return
        }

        container.insertAdjacentHTML('beforeend', html)
        container.lastElementChild.classList.add('added-by-vanilla-nested')

        // Trigger SlimSelect initialization on newly added fields
        container.dispatchEvent(new CustomEvent('vanilla-nested:fields-added', { bubbles: true }))
      },
    }))
  },
  SORTABLE_ANIMATION_MS = 300,
  FIRST_POSITION = 1,
  registerRuleReorder = (alpine) => {
    alpine.data('ruleReorder', () => ({
      sortable: null,
      init() {
        this.sortable = Sortable.create(this.$el, {
          animation: SORTABLE_ANIMATION_MS,
          handle: '.drag-handle',
          ghostClass: 'opacity-50',
          draggable: 'tbody',
          onEnd: () => {
            const tbodies = this.$el.querySelectorAll('tbody[data-rule-id]'),
              ruleIds = Array.from(tbodies, (el) => el.dataset.ruleId),
              csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

            tbodies.forEach((tbody, index) => {
              const label = tbody.querySelector('.rule-label')
              if (label) {
                const text = label.textContent,
                  updated = text.replace(/#\d+:/u, `#${index + FIRST_POSITION}:`)
                label.textContent = updated
              }
            })

            fetch('/rules/reorder', {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
              },
              body: JSON.stringify({ 'rule_ids': ruleIds }),
            }).then((response) => {
              if (!response.ok) {
                window.location.reload()
              }
            }).catch(() => {
              window.location.reload()
            })
          },
        })
      },
      destroy() {
        if (this.sortable) {
          this.sortable.destroy()
          this.sortable = null
        }
      },
    }))
  },
  registerClipboard = (alpine) => {
    const COPY_TIMEOUT = 2000
    alpine.data('clipboard', () => ({
      copied: false,
      supported: false,
      init() {
        if ('clipboard' in navigator) {
          navigator.permissions.query({ name: 'clipboard-write' }).then(result => {
            if (result.state === 'granted') {
              this.supported = true
            }
          })
        }
      },
      copy(text) {
        navigator.clipboard.writeText(text)
        this.copied = true
        setTimeout(() => { this.copied = false }, COPY_TIMEOUT)
      },
    }))
  },
  // alpine-turbo-drive-adapter 2.2.0 tags x-for clones for removal before Turbo caches the page with
  // `Object.values(el._x_lookup)`, which returns [] since Alpine 3.15.9 made `_x_lookup` a Map. The clones
  // then stay in the cached snapshot, and after Back Alpine renders a second set next to them.
  // Tag them here so the adapter's turbo:before-render handler strips them from the restored page.
  // Registered before Alpine.start() so it runs ahead of the adapter's own turbo:before-cache handler,
  // which untags clones inside [data-turbo-permanent] elements.
  tagXForClonesBeforeCache = (alpine) => {
    document.addEventListener('turbo:before-cache', () => {
      alpine.mutateDom(() => {
        document.body.querySelectorAll('[x-for]').forEach((el) => {
          // eslint-disable-next-line no-underscore-dangle
          el._x_lookup?.forEach((clone) => clone.setAttribute('data-alpine-generated-me', true))
        })
      })
    })
  }

export default function initAlpine () {
  Alpine.data('transitions', () => ({
    slideRightRevertTransition: {
      'x-transition:enter' () { return 'transition ease-in-out duration-300 transform' },
      'x-transition:enter-start' () { return '-translate-x-full' },
      'x-transition:enter-end' () { return 'translate-x-0' },
      'x-transition:leave' () { return 'transition ease-in-out duration-300 transform' },
      'x-transition:leave-start' () { return 'translate-x-0' },
      'x-transition:leave-end' () { return '-translate-x-full' },
    },
    zoomUpTransition: {
      'x-transition:enter' () { return 'transition ease-out duration-300' },
      'x-transition:enter-start' () { return 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95' },
      'x-transition:enter-end' () { return 'opacity-100 translate-y-0 sm:scale-100' },
      'x-transition:leave' () { return 'transition ease-in duration-200' },
      'x-transition:leave-start' () { return 'opacity-100 translate-y-0 sm:scale-100' },
      'x-transition:leave-end' () { return 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95' },
    },
    fadeOpacityTransition: {
      'x-transition:enter' () { return 'transition ease-linear duration-300' },
      'x-transition:enter-start' () { return 'opacity-0' },
      'x-transition:enter-end' () { return 'opacity-100' },
      'x-transition:leave' () { return 'transition ease-linear duration-300' },
      'x-transition:leave-start' () { return 'opacity-100' },
      'x-transition:leave-end' () { return 'opacity-0' },
    },
    notificationTransition: {
      'x-transition:enter' () { return 'transition ease-out duration-300 transform' },
      'x-transition:enter-start' () { return 'translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2' },
      'x-transition:enter-end' () { return 'translate-y-0 opacity-100 sm:translate-x-0' },
      'x-transition:leave' () { return 'transition ease-in-out duration-300 transform' },
      'x-transition:leave-start' () { return 'opacity-100' },
      'x-transition:leave-end' () { return 'opacity-0' },
    },
  }))

  registerClipboard(Alpine)
  registerConditionGroup(Alpine)
  registerRuleReorder(Alpine)
  registerRuleTestForm(Alpine)
  tagXForClonesBeforeCache(Alpine)

  Alpine.start()
}
/* eslint-enable sort-keys */
